import React, { useState } from "react";
import { View } from "react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { LucideIcon } from "lucide-react-native";
import { colors, semanticColors } from "@louise/moon-ui/theme/tokens";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  MoonStepper,
  SecondaryButton,
  cardSurface,
} from "@louise/moon-ui";

const REMOVE_CONFIRMATION_ILLUSTRATION =
  require("@src/apps/moon/assets/images/you-icons/trash-bin.png") as number;

export type CounterInputProps = {
  label: string;
  description?: string;
  value: number | null;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
  max?: number;
  icon?: LucideIcon;
  /** The value's unit, such as "days", shown after the counted value. */
  unit?: string;
  testID?: string;
  /** Asks before a decrease that removes an entry, as a remove confirmation. */
  decrementConfirmation?: {
    shouldConfirm: () => boolean;
    title: string;
    message: string;
    cancelLabel: string;
    confirmLabel: string;
  };
};

/**
 * Shared numeric answer card for Medical History.
 *
 * Keep the number control compact and neutral: the question card owns the
 * glass surface, while MoonStepper owns the accessible +/- interaction.
 */
export function CounterInput({
  label,
  description,
  value,
  onIncrement,
  onDecrement,
  min = 0,
  max,
  icon: Icon,
  unit,
  testID,
  decrementConfirmation,
}: CounterInputProps) {
  // Keep an unanswered counter visually empty while giving the stepper a
  // numeric value just below min so the first increment selects min.
  const numericValue = value ?? Math.max(min - 1, 0);
  // The shared dialog, not Alert.alert: the web build has no native alert,
  // so a decrease that needed confirming could never happen there.
  const [isConfirmingDecrement, setIsConfirmingDecrement] = useState(false);
  const cancelDecrement = () => setIsConfirmingDecrement(false);
  const confirmDecrement = () => {
    setIsConfirmingDecrement(false);
    onDecrement();
  };

  return (
    <View
      testID={testID}
      style={{
        ...cardSurface(),
        width: "100%",
        padding: 16,
      }}
    >
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          gap: 16,
        }}
      >
        <View
          style={{
            alignItems: "center",
            flex: 1,
            flexDirection: "row",
            gap: 12,
            minWidth: 0,
          }}
        >
          {Icon && <Icon size={18} color={semanticColors.text.secondary} />}
          <View style={{ flex: 1, gap: 3, minWidth: 0 }}>
            <Text
              className="font-dm-sans-medium"
              style={{
                color: semanticColors.text.primary,
                fontSize: 15,
                fontWeight: "600",
                lineHeight: 20,
              }}
            >
              {label}
            </Text>
            {description && (
              <Text
                className="font-dm-sans"
                style={{
                  color: semanticColors.text.tertiary,
                  fontSize: 12,
                  lineHeight: 17,
                }}
              >
                {description}
              </Text>
            )}
          </View>
        </View>
        <MoonStepper
          value={numericValue}
          min={min}
          max={max}
          // The dash stands in for an unanswered count; an answer shows its
          // number, and its unit after it.
          displayValue={value === null ? "–" : undefined}
          unit={unit}
          accentColor={colors.softBrownMid}
          style={{ flexShrink: 0 }}
          onChange={(nextValue) => {
            if (nextValue > numericValue) onIncrement();
            if (nextValue < numericValue) {
              if (decrementConfirmation?.shouldConfirm()) {
                setIsConfirmingDecrement(true);
              } else {
                onDecrement();
              }
            }
          }}
        />
      </View>
      {decrementConfirmation ? (
        <Dialog
          visible={isConfirmingDecrement}
          onClose={cancelDecrement}
          layout="centered"
          closeButton="minimal"
          testID={testID ? `${testID}-decrement-confirmation` : undefined}
        >
          <DialogIcon source={REMOVE_CONFIRMATION_ILLUSTRATION} />
          <DialogHeader
            align="center"
            title={decrementConfirmation.title}
            subtitle={decrementConfirmation.message}
            style={{ marginTop: 16, marginBottom: 20 }}
          />
          <DialogActions style={{ marginTop: 0 }}>
            <SecondaryButton
              label={decrementConfirmation.cancelLabel}
              action="cancel_decrement"
              onPress={cancelDecrement}
              flex={1}
            />
            <SecondaryButton
              variant="destructive"
              label={decrementConfirmation.confirmLabel}
              action="confirm_decrement"
              onPress={confirmDecrement}
              flex={1}
            />
          </DialogActions>
        </Dialog>
      ) : null}
    </View>
  );
}
